import { FieldViewModel, IOmniaShell, OmniaDialogSize } from "../../omnia"
import { NutrientFilter } from "../../services/nutrientFilter"
import { IFieldsService, INutritionPlan, INutritionService, IPlanEditArguments } from "../../types"
import { calculateRequirements, editPlan } from "../../utils"
import { DashboardFilters } from "./filter"
import { MapContext } from "./map-context"
import { MapFilter } from "./map-filter"
import { PlanReader } from "./plan-reader"
import { DashboardSelection } from "./selection"
import { IDashboardAction, IDashboardActionOptions, IDashboardContext, IDashboardFilters, IDashboardSettings, IEditRequirementsArguments } from "./types"

export class MapDashboardContext implements IDashboardContext {

    private _filters: IDashboardFilters;

    private _currentAction: {
        action: IDashboardAction;
        defer: ng.IDeferred<any>;
        options: IDashboardActionOptions;
    };

    private _settings: IDashboardSettings = {
        applications: {
            showNutrientRates: false,
        }
    };


    constructor(private shell: IOmniaShell, private svc: INutritionService, private fieldsSvc: IFieldsService, private state: {

        readonly nutrients: NutrientFilter,

        readonly planReader: PlanReader,

        readonly mapCtxt: MapContext,
        readonly mapFilter: MapFilter,

        selection: DashboardSelection,

        refresh: () => ng.IPromise<any>,

    }) {

        this._filters = new DashboardFilters(this.fieldsSvc)

    }

    startInitialComponent() {

        let params = this.shell.getRouteParams() || {};

        let component = params.action;
        let id = params.id;

        switch (component) {

            case "edit-plan":
                let plan = this.planReader.getPlan(id);
                if (plan) {
                    this.updatePlan(plan.plan, null);
                }
                break;

        }

    }

    get filters(): IDashboardFilters {
        return this._filters;
    }

    set filters(value: IDashboardFilters) {
        this._filters = value;
    }

    get mapCtxt(): MapContext {
        return this.state.mapCtxt;
    }

    get mapFilter(): MapFilter {
        return this.state.mapFilter;
    }

    get nutrients(): NutrientFilter {
        return this.state.nutrients;
    }

    get planReader(): PlanReader {
        return this.state.planReader;
    }

    get selection(): DashboardSelection {
        return this.state.selection;
    }

    setSelection(value: DashboardSelection, context: string) {
        this.state.selection = value;
    }

    get settings(): IDashboardSettings {
        return this._settings;
    }

    get showMenu(): boolean {
        if (!this._currentAction) return true;
        return this._currentAction.options.showMenu;
    }

    get showFilters(): boolean {
        if (!this._currentAction) return true;
        return this._currentAction.options.showFilters;
    }

    get enableContextMenu(): boolean {
        return (!this._currentAction);
    }

    refresh(): ng.IPromise<any> {
        return this.state.refresh()
    }

    getCurrentAction(): IDashboardAction {
        return (this._currentAction ? this._currentAction.action : undefined);
    }

    calculateRequirements(croppingIds: number[]): ng.IPromise<boolean> {
        return this.refreshIfOk(calculateRequirements(this.svc, this.shell, croppingIds))
    }

    resetRequirements(croppingIds: number[]): ng.IPromise<boolean> {
        return this.showDialog("omnia-nutri-requirements-reset", croppingIds, "md");
    }

    editRequirements(cropping: FieldViewModel[], wholeField?: boolean) {

        if (cropping instanceof Array) {
            this._filters = this._filters.setSubset(cropping);
        }

        let args = <IEditRequirementsArguments>{
            cropping: this._filters.cropping,
            wholeField: wholeField
        };

        let options = <IDashboardActionOptions>{
            showMenu: false,
            showFilters: true,
        };

        return this.startAction("editRequirements", args, options).then(r => {
            this._filters = this._filters.setSubset(null);
        });

    }

    editApplicationZones(): ng.IPromise<any> {

        let options = <IDashboardActionOptions>{
            showMenu: false,
            showFilters: false,
        };

        return this.startAction("editAppZones", null, options);

    }

    createPlan(croppingsIds: number[]) {

        let cropping = croppingsIds ? croppingsIds.map(id => this.fieldsSvc.findCropping(id)).filter(c => !!c) : [];

        let args: IPlanEditArguments = {
            plan: null,
            initialCropping: cropping,
        };

        return this.editPlan(args)

    }

    updatePlan(plan: INutritionPlan, args?: IPlanEditArguments) {

        args = {
            ...args || {},
            plan: plan,
            initialCropping: null
        };

        return this.editPlan(args)

    }

    private editPlan(args: IPlanEditArguments) {
        return this.refreshIfOk(editPlan(this.svc, this.shell, args))
    }

    private showDialog(componentId: string, args: any, size: OmniaDialogSize): ng.IPromise<any> {

        const p = this.shell.showComponentAsDialog(componentId, args, {
            size,
            closeOnEscape: false,
            backdrop: "static"
        });

        return this.refreshIfOk(p)

    }

    private refreshIfOk(promise: ng.IPromise<boolean>) {
        return promise.then(ok => ok
            ? this.refresh().then(_ => true)
            : ok
        )
    }

    startAction(id: string, args?: any, options?: IDashboardActionOptions): ng.IPromise<any> {

        if (this._currentAction) {
            this.endCurrentAction();
        }

        let action = {
            id: id,
            args: args
        };

        let defer = this.shell.defer();

        this._currentAction = {
            action: action,
            defer: defer,
            options: options || {},
        }

        return defer.promise.then(r => {
            if (r) {
                return this.refresh();
            }
        })

    }

    endCurrentAction(status?: any) {

        if (this._currentAction) {
            this._currentAction.defer.resolve(status);
            this._currentAction = null;
        }

    }

}
